import { useMutation } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@lawline/ui/components/dialog";
import { Label } from "@lawline/ui/components/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@lawline/ui/components/select";

import { rotateSecretMutation } from "../webhooks.mutations";
import { WebhookErrorText } from "./webhook-error-text";
import { WebhookSecret } from "./webhook-secret";

const OVERLAP_OPTIONS = [
	{ key: "now", hours: 0 },
	{ key: "hour1", hours: 1 },
	{ key: "hours24", hours: 24 },
	{ key: "hours72", hours: 72 },
] as const;

export function RotateWebhookSecret({ endpointId }: { endpointId: string }) {
	const t = useTranslations("webhooks.rotate");
	const tActions = useTranslations("webhooks.actions");
	const rotate = useMutation(rotateSecretMutation);
	const [open, setOpen] = useState(false);
	const [hours, setHours] = useState("24");
	const [secret, setSecret] = useState<string | null>(null);

	// The server rotates whether anyone waits for the answer or not, and the new secret is in that
	// answer alone, so the dialog stays open until it arrives.
	function onOpenChange(next: boolean) {
		if (!next && rotate.isPending) return;
		setOpen(next);
		if (!next) {
			setHours("24");
			rotate.reset();
			setSecret(null);
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogTrigger asChild>
				<Button variant="outline" size="sm">
					<KeyRound className="size-4" />
					{tActions("rotate")}
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-md">
				{secret ? (
					<>
						<DialogHeader>
							<DialogTitle>{t("successTitle")}</DialogTitle>
						</DialogHeader>
						<WebhookSecret secret={secret} />
						<DialogFooter>
							<Button onClick={() => onOpenChange(false)}>{t("done")}</Button>
						</DialogFooter>
					</>
				) : (
					<>
						<DialogHeader>
							<DialogTitle>{t("title")}</DialogTitle>
							<DialogDescription>{t("description")}</DialogDescription>
						</DialogHeader>
						<div className="flex flex-col gap-2">
							<Label htmlFor="webhook-overlap">{t("overlapLabel")}</Label>
							<Select value={hours} onValueChange={setHours}>
								<SelectTrigger id="webhook-overlap">
									<SelectValue />
								</SelectTrigger>
								<SelectContent position="popper">
									{OVERLAP_OPTIONS.map((option) => (
										<SelectItem key={option.key} value={String(option.hours)}>
											{t(`overlapOptions.${option.key}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{rotate.error && <WebhookErrorText error={rotate.error} />}
						<DialogFooter>
							<Button
								variant="outline"
								disabled={rotate.isPending}
								onClick={() => onOpenChange(false)}
							>
								{t("cancel")}
							</Button>
							<Button
								disabled={rotate.isPending}
								onClick={() =>
									rotate.mutate(
										{ id: endpointId, expiresInHours: Number(hours) },
										{ onSuccess: (row) => setSecret(row.secret) },
									)
								}
							>
								{rotate.isPending ? t("submitLoading") : t("submit")}
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
